import { CheckSquare, Plus, RotateCcw, Users, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../ui/components/ui/dialog';
import {
  OrganizationRecipientResponseDto,
  OrganizationStatusSelectableOptionResponseDto,
  RecipientListPaginatedResponseDto
} from '../types/response';
import StarIcon from '../../../ui/components/icons/star-icon';
import RocketIcon from '../../../ui/components/icons/rocket-icon';
import ContractIcon from '../../../ui/components/icons/contract-icon';
import BankIcon from '../../../ui/components/icons/bank-icon';
import Preloader from '../../../ui/components/ui/preloader';
import { useCallback, useEffect, useRef, useState } from 'react';
import useApiRequest from '../../../lib/hooks/useRequest';
import { Button } from '../../../ui/components/ui/button';
import { RecipientFilterQueryDto, RecipientFilterRequestDto } from '../types/request';
import { Input } from '../../../ui/components/ui/input';

const getStatusIcon = (icon: string) => {
  switch (icon) {
    case 'stars':
      return <StarIcon />;
    case 'rocket':
      return <RocketIcon />;
    case 'contract':
      return <ContractIcon />;
    case 'bank':
      return <BankIcon />;
    default:
      return <ContractIcon />;
  }
};

const getColorStyle = (color: string) => {
  return {
    default: {
      borderColor: color,
      color: color,
      backgroundColor: `${color}33`, // Hex with 20% opacity
      opacity: 1
    },
    hover: {
      borderColor: `${color}`,
      color: `${color}`,
      backgroundColor: `${color}33`, // Hex with 40% opacity
      opacity: 0.6
    },
    unselected: {
      borderColor: 'lightgray',
      color: 'gray',
      backgroundColor: 'lightgray', // Hex with 20% opacity
      opacity: 0.6
    }
  };
};

interface RecipientSelectionDialogProps {
  isOpen: boolean;
  orgaStatus: OrganizationStatusSelectableOptionResponseDto[];
  selectedRecipients?: OrganizationRecipientResponseDto[];
  handleFinish: (recipients: OrganizationRecipientResponseDto[]) => void;
  abortDialog: () => void;
}

export const RecipientSelectionDialog = ({
  isOpen,
  orgaStatus,
  selectedRecipients = [],
  handleFinish,
  abortDialog
}: RecipientSelectionDialogProps) => {
  const handleOpenChange = (open: boolean): void => {
    if (!open) {
      abortDialog();
      return;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-7xl w-full h-[90vh] p-0 flex flex-col">
        <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
          <DialogTitle>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Users className="w-6 h-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-gray-900">Empfänger Auswählen</h2>
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>
        {isOpen ? (
          <RecipientSelectionDialogContent handleFinish={handleFinish} alreadySelectedRecipients={selectedRecipients} orgaStatus={orgaStatus} />
        ) : (
          <Preloader />
        )}
      </DialogContent>
    </Dialog>
  );
};

interface RecipientSelectionDialogContentProps {
  handleFinish: (recipients: OrganizationRecipientResponseDto[]) => void;
  alreadySelectedRecipients?: OrganizationRecipientResponseDto[];
  orgaStatus: OrganizationStatusSelectableOptionResponseDto[];
}

const RecipientSelectionDialogContent = ({ handleFinish, alreadySelectedRecipients = [], orgaStatus }: RecipientSelectionDialogContentProps) => {
  const { apiRequest } = useApiRequest();

  const [isLoading, setIsLoading] = useState(true);
  const [recipients, setRecipients] = useState<(OrganizationRecipientResponseDto & { show: boolean })[]>([]);
  const [selectedRecipients, setSelectedRecipients] = useState<OrganizationRecipientResponseDto[]>(alreadySelectedRecipients);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [hoveredStatus, setHoveredStatus] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchQueryValue, setSearchQueryValue] = useState<string>('');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const filterChangedRef = useRef<boolean>(false);

  const fetchAllRecipients = useCallback(
    async (searchQuery: string = '', selectedStatuses: string[] = [], excludeOrganizationIds: string[] = []) => {
      setIsLoading(true);

      try {
        const filterBody: RecipientFilterRequestDto = {
          excludeOrganizationIds: excludeOrganizationIds
        };

        if (selectedStatuses.length > 0) {
          filterBody.status = selectedStatuses;
        }

        const queryParams: RecipientFilterQueryDto = {};
        if (searchQuery.trim().length >= 2) {
          queryParams.search = searchQuery.trim();
        }

        let url = `mass-mails/internal/recipients/all`;
        if (queryParams.search) {
          url += `?search=${encodeURIComponent(queryParams.search)}`;
        }

        const response = await apiRequest<OrganizationRecipientResponseDto[]>(url, 'POST', {
          body: filterBody
        });

        if (response.data) {
          const newRecipients = response.data.filter((r) => !excludeOrganizationIds.includes(r._id));
          setSelectedRecipients((prev) => [...prev, ...newRecipients]);

          const newRecipientsIds = newRecipients.map((r) => r._id);
          setRecipients((prev) => prev.map((r) => ({ ...r, show: !newRecipientsIds.includes(r._id) && !excludeOrganizationIds.includes(r._id) })));
        }
      } catch (error) {
        console.error('Error fetching all recipients:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [apiRequest]
  );

  const fetchRecipients = useCallback(
    async (pageNum: number, isInitial: boolean = false, selectedRecipients: string[], searchQuery: string = '', selectedStatuses: string[] = []) => {
      if (isInitial) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const filterBody: RecipientFilterRequestDto = {};
        if (selectedStatuses.length > 0) {
          filterBody.status = selectedStatuses;
        }

        const queryParams: RecipientFilterQueryDto = {};
        if (searchQuery.trim().length >= 2) {
          queryParams.search = searchQuery.trim();
        }

        let url = `mass-mails/internal/recipients?page=${pageNum}&limit=10`;

        if (queryParams.search) {
          url += `&search=${encodeURIComponent(queryParams.search)}`;
        }

        const response = await apiRequest<RecipientListPaginatedResponseDto>(url, 'POST', {
          body: filterBody
        });

        if (response.data) {
          if (isInitial) {
            setRecipients(response.data.docs.map((r) => ({ ...r, show: true })));
          } else {
            setRecipients((prev) => [...prev, ...response.data!.docs.map((r) => ({ ...r, show: true }))]);
          }
          setHasMore(response.data.hasNextPage);
          setPage(response.data.page);

          setRecipients((prev) => prev.map((r) => ({ ...r, show: !selectedRecipients.includes(r._id) })));
        }
      } catch (error) {
        console.error('Error fetching recipients:', error);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [apiRequest]
  );

  // only initial load
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    setRecipients([]);
    fetchRecipients(
      1,
      true,
      alreadySelectedRecipients.map((r) => r._id)
    );
  }, [fetchRecipients, alreadySelectedRecipients]);

  // Handler for infinite scroll, reloading and filtering
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || isLoading || isLoadingMore) return;

    if (filterChangedRef.current) {
      filterChangedRef.current = false;
      setPage(1);
      setHasMore(true);
      setRecipients([]);
      fetchRecipients(
        1,
        true,
        selectedRecipients.map((r) => r._id),
        searchQuery,
        selectedStatuses
      );
      return;
    }

    if (!hasMore) return;

    const checkAndLoadMore = () => {
      if (isLoading || isLoadingMore || !hasMore) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const isScrollable = scrollHeight > clientHeight;

      const needsMoreScrollingNotPossible = !isScrollable && scrollHeight <= clientHeight;
      const needsMoreScrollingPossible = isScrollable && scrollHeight - scrollTop <= clientHeight + 100;

      const needsMore = needsMoreScrollingNotPossible || needsMoreScrollingPossible;

      if (!needsMore) return;

      if (needsMoreScrollingNotPossible) {
        fetchRecipients(
          page + 1,
          false,
          selectedRecipients.map((r) => r._id),
          searchQuery,
          selectedStatuses
        );
        return;
      }

      if (needsMoreScrollingPossible) {
        fetchRecipients(
          page + 1,
          false,
          selectedRecipients.map((r) => r._id),
          searchQuery,
          selectedStatuses
        );
      }
    };

    const handleScroll = () => {
      checkAndLoadMore();
    };

    checkAndLoadMore();
    const timeoutId = setTimeout(checkAndLoadMore, 100);

    container.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(timeoutId);
      container.removeEventListener('scroll', handleScroll);
    };
  }, [hasMore, isLoadingMore, isLoading, page, fetchRecipients, selectedRecipients, searchQuery, selectedStatuses]);

  const handleAddRecipient = (recipientId: string) => {
    const recipient = recipients.find((r) => r._id === recipientId);
    if (!recipient || !recipient.show) return;

    const existingRecipient = selectedRecipients.find((r) => r._id === recipientId);
    if (existingRecipient) return;

    setSelectedRecipients((prev) => [...prev, recipient]);
    setRecipients((prev) => prev.map((r) => (r._id === recipientId ? { ...r, show: false } : r)));
  };

  const handleRemoveRecipient = (recipientId: string) => {
    const newSelectedRecipients = selectedRecipients.filter((r) => r._id !== recipientId);
    setSelectedRecipients(newSelectedRecipients);

    setRecipients((prev) => prev.map((r) => (r._id === recipientId ? { ...r, show: true } : r)));
  };

  const handleSearchQueryChange = (value: string) => {
    filterChangedRef.current = true;
    setSearchQueryValue(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setSearchQuery(value);
    }, 1000);
  };

  const handleStatusClick = (status: string) => {
    filterChangedRef.current = true;
    setSelectedStatuses((prev) => {
      if (prev.includes(status)) {
        return prev.filter((s) => s !== status);
      }
      return [...prev, status];
    });
  };

  const handleSelectAll = () => {
    fetchAllRecipients(
      searchQuery,
      selectedStatuses,
      selectedRecipients.map((r) => r._id)
    );
  };

  const handleResetSearch = () => {
    setSearchQueryValue('');
    setSearchQuery('');
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    filterChangedRef.current = true;
  };

  const handleResetAllFilters = () => {
    setSearchQueryValue('');
    setSearchQuery('');
    setSelectedStatuses([]);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    filterChangedRef.current = true;
  };

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Left Panel */}
      <div className="w-1/2 border-r flex flex-col">
        {/* Header / Filters */}
        <div className="p-4 flex flex-col gap-3 flex-shrink-0">
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <Input
                placeholder="Suche nach Name, Kundennummer oder E-Mail..."
                value={searchQueryValue}
                onChange={(e) => handleSearchQueryChange(e.target.value)}
                className="flex-1 pr-8"
              />
              {searchQueryValue && (
                <button onClick={handleResetSearch} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {orgaStatus.map((status) => {
              const colorStyle = getColorStyle(status.color);
              const icon = getStatusIcon(status.icon);

              const isSelected = selectedStatuses.includes(status.id);
              const isHovered = hoveredStatus === status.id;

              let appliedStyle = colorStyle.unselected;
              if (isSelected) {
                appliedStyle = colorStyle.default;
              } else if (isHovered) {
                appliedStyle = colorStyle.hover;
              }

              return (
                <div
                  key={status.id}
                  className="flex items-center gap-2 text-sm cursor-pointer"
                  onMouseEnter={() => setHoveredStatus(status.id)}
                  onMouseLeave={() => setHoveredStatus(null)}
                  onClick={() => handleStatusClick(status.id)}>
                  <div className="border w-fit rounded-2xl px-3 py-1 font-bold flex gap-2 items-center" style={appliedStyle}>
                    {icon}
                    {status.value} ({status.count})
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border-b flex flex-col gap-3 flex-shrink-0">
          {(searchQuery || selectedStatuses.length > 0) && (
            <div className="p-4 pt-0 flex gap-2 items-center justify-between">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleResetAllFilters} className="gap-2">
                  <RotateCcw className="w-4 h-4" />
                  Filter zurücksetzen
                </Button>
              </div>
              <Button variant="default" size="sm" onClick={handleSelectAll} className="gap-2">
                <CheckSquare className="w-4 h-4" />
                Alle Ergebnisse auswählen
              </Button>
            </div>
          )}
        </div>

        {/* Search Results with Infinite Scroll */}
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center items-center py-10">
              <Preloader />
            </div>
          ) : recipients.length <= 0 ? (
            <div className="flex justify-center items-center py-10 text-gray-500">Keine Kunden gefunden.</div>
          ) : (
            <div className="p-4 space-y-2">
              {recipients.map((recipient) => (
                <RecipientItem
                  key={recipient._id}
                  recipient={recipient}
                  onButtonClick={handleAddRecipient}
                  selected={false}
                  show={recipient.show}
                  orgaStatus={orgaStatus}
                />
              ))}
              {isLoadingMore && (
                <div className="flex justify-center py-4">
                  <Preloader />
                </div>
              )}
              {!hasMore && recipients.length > 0 && <div className="text-center text-sm text-gray-500 py-4">Keine weiteren Ergebnisse</div>}
            </div>
          )}
        </div>
      </div>
      {/* Right Panel */}
      <div className="w-1/2 flex flex-col">
        <div className="p-4 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Ausgewählte Empfänger</h3>
            <div className="flex items-center gap-1">
              {selectedRecipients.length > 0 && (
                <span
                  className="cursor-pointer"
                  onClick={() => {
                    setSelectedRecipients([]);
                    setRecipients((prev) => prev.map((r) => ({ ...r, show: true })));
                  }}>
                  <X className="w-3 h-3" />
                </span>
              )}
              <span className="text-sm text-gray-500">{selectedRecipients.length} ausgewählt</span>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {selectedRecipients.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <Users className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-sm">Noch keine Empfänger ausgewählt</p>
              <p className="text-xs mt-2">Wähle Empfänger aus der linken Liste aus</p>
            </div>
          ) : (
            <div className="space-y-2">
              {selectedRecipients.map((recipient) => (
                <RecipientItem
                  key={recipient._id}
                  recipient={recipient}
                  onButtonClick={handleRemoveRecipient}
                  selected={true}
                  orgaStatus={orgaStatus}
                />
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t flex-shrink-0">
          <Button onClick={() => handleFinish(selectedRecipients)} className="w-full" size="lg">
            Übernehmen ({selectedRecipients.length} Empfänger)
          </Button>
        </div>
      </div>
    </div>
  );
};

const RecipientItem = ({
  recipient,
  onButtonClick,
  orgaStatus,
  selected = false,
  show = true
}: {
  recipient: OrganizationRecipientResponseDto;
  onButtonClick: (recipientId: string) => void;
  orgaStatus: OrganizationStatusSelectableOptionResponseDto[];
  selected?: boolean;
  show?: boolean;
}) => {
  const gradientBackground = selected
    ? {
        background: `
          radial-gradient(circle at 10% 10%, rgba(95, 213, 222, 0.1) 0%, transparent 40%),
          radial-gradient(circle at 90% 90%, rgba(192, 222, 96, 0.1) 0%, transparent 40%),
          linear-gradient(135deg, rgba(95, 213, 222, 0.15) 0%, rgba(192, 222, 96, 0.15) 100%)
        `
      }
    : {};

  const status = orgaStatus.find((s) => s.id === recipient.belongsToStatus);

  return (
    <div
      key={recipient._id}
      className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
        !show ? 'bg-red-50' : selected ? 'border-blue-300' : 'hover:bg-gray-50'
      }`}
      style={gradientBackground}>
      {' '}
      <div className="flex-1">
        <div className="font-medium">
          {recipient.name} ({recipient.customerNumber})
        </div>
        <div className="text-sm text-gray-500 flex items-center gap-1">
          {status?.icon && getStatusIcon(status.icon)} {status?.value}
        </div>
        {recipient.adminMail && <div className="text-xs text-gray-400">{recipient.adminMail}</div>}
      </div>
      {selected || show ? (
        <Button
          className={`ml-2 ${selected ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}`}
          variant="ghost"
          size="sm"
          onClick={() => onButtonClick(recipient._id)}>
          {selected ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </Button>
      ) : null}
    </div>
  );
};
